import CallIcon from '@mui/icons-material/Call';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PsychologyIcon from '@mui/icons-material/Psychology';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CenteredContainer from 'components/common/CenteredContainer';
import Conversation from 'components/common/Conversation';
import CreateTicketDialog from 'components/common/CreateTicketDialog';
import QuickTemplates from 'components/common/QuickTemplates';
import { useBusinessUnits } from 'contexts/BusinessUnitsContext';
import useCurrentUser from 'hooks/useCurrentUser';
import ChatFooter from 'pages/Client/ChatFooter';
import TemplateMessageModal from 'pages/Client/TemplateMessageModal';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { signInUser } from 'redux/slices/user.slice';
import {
  useCreateClientMutation,
  useGetClientQuery,
  useGetClientsQuery,
  useUpdateEditableAttributesMutation,
} from 'services/api/clients.api';
import { useGetClientMessagesQuery, useGetNewClientMessagesMutation } from 'services/api/messages';
import { useGetUserQuery } from 'services/api/user.api';
import type { Message, MessageStatus } from 'services/types/message';
import type { ScheduledMessage } from 'services/types/scheduledMessage';
import { AUTOSRELIF_BUSINESS_UNIT_ID } from 'utils/constants';
import { subscribeChannel } from 'utils/pusherClient';
import { isWhatsAppWindowOpen } from 'utils/whatsAppWindow';

const ALLOWED_PARENT_ORIGINS = [
  // Same-origin embedding (e.g. the AutosRelif Demo page loads /autosrelif-client in an iframe).
  window.location.origin,
];

const NODE_ENV = import.meta.env.NODE_ENV;
if (NODE_ENV === 'development') {
  ALLOWED_PARENT_ORIGINS.push('http://localhost:3000');
}

interface RelifTokenMessage {
  eventType: string;
  token: string;
  name?: string;
  email?: string;
  role?: 'seller' | 'businessUnitAdmin';
  branchOffice?: string;
}

const RelifCRM = () => {
  const [searchParams] = useSearchParams();
  const clientIdParam = searchParams.get('clientId');
  const phoneParam = searchParams.get('phone');
  const nameParam = searchParams.get('name');
  const lastNameParam = searchParams.get('lastName');
  const emailParam = searchParams.get('email');
  const rutParam = searchParams.get('rut');
  const brandParam = searchParams.get('brand');
  const modelParam = searchParams.get('model');

  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const [authError, setAuthError] = useState<string | null>(null);
  const [receivedToken, setReceivedToken] = useState<string | null>(null);
  const hasReceivedToken = useRef(false);
  const [conversation, setConversation] = useState<Message[]>([]);
  const [scheduledMessages, setScheduledMessages] = useState<ScheduledMessage[]>([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);
  const [openTemplateModal, setOpenTemplateModal] = useState(false);
  const [openCreateTicketModal, setOpenCreateTicketModal] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(clientIdParam || null);
  const [formData, setFormData] = useState({
    firstName: nameParam || '',
    lastName: lastNameParam || '',
    email: emailParam || '',
    rut: rutParam || '',
    businessUnitId: null as number | null,
    brand: brandParam || '',
    model: modelParam || '',
  });

  const { businessUnits } = useBusinessUnits();
  const [createClient] = useCreateClientMutation();

  // Filter business units to only show autosrelif ones
  const autosRelifBusinessUnits = businessUnits?.filter((bu) => bu.id === AUTOSRELIF_BUSINESS_UNIT_ID) || [];

  // Fetch user data after receiving token
  const { data: userData, isLoading: isLoadingUser } = useGetUserQuery(null, {
    skip: !receivedToken,
  });

  // Fetch clients by phone if phone parameter is provided
  const {
    data: clientsDataByPhone,
    isLoading: isLoadingClientsByPhone,
    error: errorLoadingClientsByPhone,
  } = useGetClientsQuery(
    {
      paginationModel: { page: 0, pageSize: 10 },
      filterModel: {
        items: [{ field: 'phone', operator: 'equals', value: phoneParam }],
      },
      sortModel: [],
    },
    {
      skip: !receivedToken || !phoneParam || !!selectedClientId,
    }
  );

  const clientsByPhone = clientsDataByPhone?.clients;

  // Skip queries until authenticated and client is selected
  const {
    data: client,
    isLoading: isLoadingClient,
    refetch: refetchClient,
  } = useGetClientQuery(selectedClientId || '', {
    skip: !receivedToken || !selectedClientId,
  });

  const {
    data: messages,
    isLoading: isLoadingMessages,
    refetch: refetchMessages,
  } = useGetClientMessagesQuery(selectedClientId || '', {
    skip: !receivedToken || !selectedClientId,
  });

  const [getNewClientMessages, { data: messagesData }] = useGetNewClientMessagesMutation();
  const [updateEditableAttributes] = useUpdateEditableAttributesMutation();

  // Update user data when fetched from API
  useEffect(() => {
    if (userData && receivedToken) {
      // Update Redux with complete user data from API
      dispatch(
        signInUser({
          ...userData,
          accessToken: receivedToken, // Keep the token we received via postMessage
        })
      );

      console.log('[RelifCRM] User data loaded and authenticated:', userData);
    }
  }, [userData, receivedToken, dispatch]);

  // Auto-select client if only one is found by phone
  useEffect(() => {
    if (clientsByPhone && clientsByPhone.length === 1 && !selectedClientId) {
      setSelectedClientId(clientsByPhone[0].id.toString());
    }
  }, [clientsByPhone, selectedClientId]);

  // Update conversation when messages are loaded
  useEffect(() => {
    if (messages) {
      setConversation(messages);
    }
  }, [messages]);

  // Update scheduled messages when client data is loaded
  useEffect(() => {
    if (client?.scheduledMessages) {
      setScheduledMessages(client.scheduledMessages);
    }
  }, [client]);

  /**
   * Handle update message status
   */
  const handleUpdateMessageStatus = (
    updatedMessages: { id: number; status: MessageStatus }[],
    refetchStatusMessages: boolean
  ) => {
    setConversation((prevConversation) =>
      prevConversation.map((m) => {
        const newStatus = updatedMessages.find((um) => um.id === m.id)?.status;
        if (newStatus) {
          return { ...m, status: newStatus };
        }
        return m;
      })
    );
    const notFoundMessages = updatedMessages
      .filter((um) => !conversation.find((m) => m.id === um.id))
      .map((um) => um.id);
    if (notFoundMessages.length > 0 && refetchStatusMessages && selectedClientId) {
      getNewClientMessages({ clientId: selectedClientId, messageId: Math.min(...notFoundMessages) }).catch((err) => {
        console.error(err);
      });
    }
  };

  // Handle new messages from mutation
  // biome-ignore lint/correctness/useExhaustiveDependencies: refetchClient and handleUpdateMessageStatus refs change per render and would loop
  useEffect(() => {
    if (messagesData) {
      setConversation((prevConversation) => {
        const updatedConversation = [...prevConversation];
        return messagesData.newMessages
          .reduce((acc, newMessage) => {
            const existingIndex = acc.findIndex((m) => m.id === newMessage.id);
            if (existingIndex !== -1) {
              acc[existingIndex] = newMessage;
            } else {
              acc.push(newMessage);
            }
            return acc;
          }, updatedConversation)
          .sort((a, b) => a.id - b.id);
      });
      handleUpdateMessageStatus(messagesData.oldMessagesStatuses, false);
      // The 24h window lives on the client, not on the loaded conversation, so an inbound message that
      // arrives while the chat is open only reopens it once the client is refetched.
      if (messagesData.newMessages.some((message) => message.role === 'user')) {
        refetchClient().catch((err) => {
          console.error(err);
        });
      }
    }
  }, [messagesData]);

  // Pusher - Listen for new messages
  useEffect(() => {
    if (client?.id) {
      const channelName = `chat-${client.id}`;
      const { channel, unsubscribe } = subscribeChannel(channelName);
      const newMessageHandler = (data: { messageId: number }) => {
        if (selectedClientId) {
          getNewClientMessages({ clientId: selectedClientId, messageId: data.messageId }).catch((err) => {
            console.error(err);
          });
        }
      };
      const updateMessagesHandler = (data: { messages: { id: number; status: MessageStatus }[] }) => {
        handleUpdateMessageStatus(data.messages, true);
      };
      channel.bind('new-message', newMessageHandler);
      channel.bind('update-messages', updateMessagesHandler);
      return () => {
        channel.unbind('new-message', newMessageHandler);
        channel.unbind('update-messages', updateMessagesHandler);
        unsubscribe();
      };
    }
    return () => {};
  }, [client?.id, selectedClientId]);

  const handleFormChange = (field: string, value: string | number | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.businessUnitId || !phoneParam) {
      return;
    }

    try {
      const createdClient = await createClient({
        businessUnitId: formData.businessUnitId,
        firstName: formData.firstName || undefined,
        phone: phoneParam,
        lastName: formData.lastName || undefined,
        email: formData.email || undefined,
        rut: formData.rut || undefined,
        brandOfInterest: formData.brand ? formData.brand.toUpperCase() : undefined,
        modelsOfInterest: formData.model ? [`${formData.model.toUpperCase()}`] : [],
        source: 'Iframe Autosystem',
        // Created by a seller from the iframe — keep AI off so the seller
        // drives the conversation instead of getting auto-replies.
        disabledAutomaticResponses: true,
      }).unwrap();

      const clientId = createdClient.id;
      setSelectedClientId(clientId.toString());
    } catch (error) {
      console.error('Error creating client:', error);
    }
  };

  // Listen for postMessage from parent window
  useEffect(() => {
    const handlePostMessageFromWindowParent = (event: MessageEvent<RelifTokenMessage>) => {
      if (!ALLOWED_PARENT_ORIGINS.includes(event.origin as (typeof ALLOWED_PARENT_ORIGINS)[number])) {
        return;
      }
      if (event.data?.eventType === 'RELIF_USER_TOKEN' && event.data?.token && !hasReceivedToken.current) {
        hasReceivedToken.current = true;
        const { token } = event.data;

        // First, save token to Redux so that useGetUserQuery can use it
        dispatch(
          signInUser({
            id: 1000000, // Temporary, will be updated with real user data
            email: '',
            accessToken: token,
            isAdmin: false,
            businessUnitIds: [],
            filterConversationsByDefault: true,
            isTestUser: false,
            createdAt: '',
            updatedAt: '',
            name: null,
            phone: null,
            canAssignClient: false,
            isAssignable: false,
            role: 'seller',
            companyId: 0,
          })
        );

        setReceivedToken(token);
        console.log('[RelifCRM] Token received, fetching user data...');
      }
    };

    window.addEventListener('message', handlePostMessageFromWindowParent);

    // Request token from parent on mount
    if (window.parent && window.parent !== window) {
      // Resolve the real parent origin from the referrer and only signal it if allowed.
      // Broadcasting to every allowed origin throws a console error for each one that
      // doesn't match the actual parent ("target origin provided does not match...").
      let parentOrigin: string | null = null;
      try {
        parentOrigin = document.referrer ? new URL(document.referrer).origin : null;
      } catch {
        parentOrigin = null;
      }
      if (parentOrigin && ALLOWED_PARENT_ORIGINS.includes(parentOrigin)) {
        window.parent.postMessage({ eventType: 'RELIF_READY' }, parentOrigin);
      }
    }

    // Timeout for authentication
    const timeout = setTimeout(() => {
      if (!hasReceivedToken.current) {
        setAuthError('No se recibió el token de autenticación. Por favor, recargue la página.');
      }
    }, 10000); // 10 seconds timeout

    return () => {
      window.removeEventListener('message', handlePostMessageFromWindowParent);
      clearTimeout(timeout);
    };
  }, [dispatch]);

  // Loading state
  if ((!receivedToken || isLoadingUser) && !authError) {
    return (
      <CenteredContainer>
        <CircularProgress size={60} />
        <Typography
          variant="h6"
          sx={{
            mt: 3,
            color: 'text.secondary',
          }}
        >
          {receivedToken ? 'Cargando datos del usuario...' : 'Esperando autenticación...'}
        </Typography>
      </CenteredContainer>
    );
  }

  // Error state
  if (authError) {
    return (
      <CenteredContainer>
        <Typography
          variant="h5"
          color="error"
          sx={{
            textAlign: 'center',
          }}
        >
          Error de Autenticación
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          {authError}
        </Typography>
      </CenteredContainer>
    );
  }

  // Loading clients by phone
  if (phoneParam && isLoadingClientsByPhone) {
    return (
      <CenteredContainer>
        <CircularProgress size={60} />
        <Typography
          variant="h6"
          sx={{
            mt: 3,
            color: 'text.secondary',
          }}
        >
          Buscando clientes con el teléfono {phoneParam}...
        </Typography>
      </CenteredContainer>
    );
  }

  // Error loading clients by phone
  if (phoneParam && errorLoadingClientsByPhone) {
    return (
      <CenteredContainer>
        <Typography variant="h5" color="error">
          Error al buscar clientes
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            color: 'text.secondary',
          }}
        >
          No se pudieron cargar los clientes con el teléfono {phoneParam}.
        </Typography>
      </CenteredContainer>
    );
  }

  // No clients found by phone
  if (phoneParam && clientsByPhone && clientsByPhone.length === 0 && !selectedClientId) {
    return (
      <CenteredContainer>
        <Typography variant="h5" color="error">
          No hay una conversación con este cliente.
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            mb: 2,
            color: 'text.secondary',
          }}
        >
          Completa los campos requeridos. (*)
        </Typography>
        <Box
          component="form"
          onSubmit={(e) => {
            handleFormSubmit(e).catch((error) => {
              console.error('Unhandled form submission error:', error);
            });
          }}
          sx={{
            width: '100%',
            maxWidth: '500px',
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
            p: 3,
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <TextField label="Teléfono" value={phoneParam} name="phone" disabled />
          <FormControl fullWidth required>
            <InputLabel id="business-unit-label">Unidad de negocio</InputLabel>
            <Select
              labelId="business-unit-label"
              label="Unidad de negocio"
              value={formData.businessUnitId || ''}
              onChange={(e) => handleFormChange('businessUnitId', e.target.value as number)}
            >
              {autosRelifBusinessUnits.map((bu) => (
                <MenuItem key={bu.id} value={bu.id}>
                  {bu.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Nombre"
            name="firstName"
            value={formData.firstName}
            onChange={(e) => handleFormChange('firstName', e.target.value)}
          />
          <TextField
            label="Apellido"
            name="lastName"
            value={formData.lastName}
            onChange={(e) => handleFormChange('lastName', e.target.value)}
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleFormChange('email', e.target.value)}
          />
          <TextField
            label="RUT"
            name="rut"
            value={formData.rut}
            onChange={(e) => handleFormChange('rut', e.target.value)}
          />
          <TextField
            label="Marca"
            name="brand"
            value={formData.brand}
            onChange={(e) => handleFormChange('brand', e.target.value)}
          />
          <TextField
            label="Modelo"
            name="model"
            value={formData.model}
            onChange={(e) => handleFormChange('model', e.target.value)}
          />
          <Button type="submit" variant="contained" disabled={!formData.businessUnitId}>
            Iniciar Conversación
          </Button>
        </Box>
      </CenteredContainer>
    );
  }

  // Multiple clients found - show selection UI
  if (phoneParam && clientsByPhone && clientsByPhone.length > 1 && !selectedClientId) {
    return (
      <CenteredContainer>
        <Typography
          variant="h5"
          sx={{
            mb: 3,
            textAlign: 'center',
          }}
        >
          Selecciona un cliente
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mb: 3,
            color: 'text.secondary',
            textAlign: 'center',
          }}
        >
          Se encontraron {clientsByPhone.length} clientes con el teléfono {phoneParam}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '100%',
            maxWidth: '500px',
          }}
        >
          {clientsByPhone.map((clientOption) => (
            <Box
              key={clientOption.id}
              onClick={() => setSelectedClientId(clientOption.id.toString())}
              sx={{
                p: 3,
                bgcolor: 'white',
                borderRadius: 2,
                boxShadow: 2,
                cursor: 'pointer',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  mb: 1,
                }}
              >
                {clientOption.firstName} {clientOption.lastName}
              </Typography>
              {clientOption.businessUnit && (
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{
                    fontWeight: 'medium',
                  }}
                >
                  {clientOption.businessUnit.name}
                </Typography>
              )}
              {clientOption.email && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mt: 0.5,
                  }}
                >
                  {clientOption.email}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mt: 0.5,
                }}
              >
                {clientOption.phone}
              </Typography>
            </Box>
          ))}
        </Box>
      </CenteredContainer>
    );
  }

  // Loading client data
  if (isLoadingClient) {
    return (
      <CenteredContainer>
        <CircularProgress size={60} />
        <Typography
          variant="h6"
          sx={{
            mt: 3,
            color: 'text.secondary',
          }}
        >
          Cargando información del cliente...
        </Typography>
      </CenteredContainer>
    );
  }

  // Client not found
  if (!client) {
    return (
      <CenteredContainer>
        <Typography variant="h5" color="error">
          Cliente no encontrado
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            color: 'text.secondary',
          }}
        >
          El cliente con ID {selectedClientId} no existe o no tienes permisos para verlo.
        </Typography>
      </CenteredContainer>
    );
  }

  const isConversationActive = isWhatsAppWindowOpen(client?.clientLastMessageDate);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleCall = () => {
    window.open(`tel:${client?.phone}`, '_self');
    handleMenuClose();
  };

  const handleToggleAI = () => {
    if (client) {
      updateEditableAttributes({
        id: client.id,
        body: {
          disabledAutomaticResponses: !client.disabledAutomaticResponses,
        },
      })
        .then(() => {
          refetchClient().catch((err) => {
            console.error(err);
          });
        })
        .catch(() => {
          console.error('Error updating client');
        });
    }
    handleMenuClose();
  };

  const handleSendTemplate = () => {
    setOpenTemplateModal(true);
    handleMenuClose();
  };

  const handleCreateTicket = () => {
    setOpenCreateTicketModal(true);
    handleMenuClose();
  };

  const handleRefreshConversation = () => {
    refetchClient().catch((err) => {
      console.error(err);
    });
    refetchMessages().catch((err) => {
      console.error(err);
    });
    handleMenuClose();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        bgcolor: '#f5f5f5',
        overflow: 'hidden',
      }}
    >
      {/* Header - White bar with email, phone and menu */}
      <Box
        sx={{
          bgcolor: 'white',
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          boxShadow: 1,
        }}
      >
        <Box
          sx={{
            flex: 1,
          }}
        >
          {client.email && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'medium',
                color: 'text.primary',
              }}
            >
              {client.email}
            </Typography>
          )}
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {client.phone}
          </Typography>
        </Box>

        {/* Menu button */}
        <IconButton onClick={handleMenuOpen} sx={{ color: 'text.primary' }}>
          <MoreVertIcon />
        </IconButton>

        <Menu anchorEl={menuAnchorEl} open={menuOpen} onClose={handleMenuClose}>
          <MenuItem onClick={handleCall}>
            <ListItemIcon>
              <CallIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Llamar</ListItemText>
          </MenuItem>

          <MenuItem onClick={handleToggleAI}>
            <ListItemIcon>
              <PsychologyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{client.disabledAutomaticResponses ? 'Habilitar IA' : 'Deshabilitar IA'}</ListItemText>
          </MenuItem>

          <MenuItem onClick={handleSendTemplate}>
            <ListItemIcon>
              <SendIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Enviar plantilla</ListItemText>
          </MenuItem>

          <MenuItem onClick={handleCreateTicket}>
            <ListItemIcon>
              <SupportAgentIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Crear ticket de soporte</ListItemText>
          </MenuItem>

          <MenuItem onClick={handleRefreshConversation}>
            <ListItemIcon>
              <RefreshIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Recargar conversación</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
      {/* Chat area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            backgroundImage: 'url("/whatsapp-background.png")',
            backgroundColor: '#efeae2',
            backgroundRepeat: 'repeat',
            backgroundSize: '412.5px 749.25px',
            overflow: 'auto',
          }}
        >
          {isLoadingMessages ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Conversation
              conversation={conversation}
              isFilteredConversation={false}
              fullHeight
              scheduledMessages={scheduledMessages}
            />
          )}
        </Box>

        {/* Chat input footer */}
        <Box>
          {isConversationActive ? (
            <ChatFooter
              client={client}
              conversation={conversation}
              setConversation={setConversation}
              addScheduledMessage={(message) => setScheduledMessages((curr) => [...curr, message])}
              isConversationActive={isConversationActive}
              newMessagePrefix={userData?.name ? `*${userData.name}*:\n` : null}
              onOpenTemplateModal={handleSendTemplate}
            />
          ) : (
            <QuickTemplates
              client={client}
              isEmptyConversation={conversation.length === 0}
              onOpenFullModal={handleSendTemplate}
              onSent={handleRefreshConversation}
            />
          )}
        </Box>
      </Box>
      {/* Template Modal */}
      {openTemplateModal && (
        <TemplateMessageModal
          client={client}
          businessUnitId={client.businessUnitId}
          open={openTemplateModal}
          setOpen={setOpenTemplateModal}
          refetch={() => {
            refetchClient().catch((err) => {
              console.error(err);
            });
          }}
          allowTemplateCreation={currentUser?.allowTemplateCreation || false}
        />
      )}
      {/* Create Ticket Modal */}
      {openCreateTicketModal && (
        <CreateTicketDialog
          clientId={client.id}
          open={openCreateTicketModal}
          onClose={() => setOpenCreateTicketModal(false)}
        />
      )}
    </Box>
  );
};

export default RelifCRM;
